import { useEffect, useRef, useState } from 'react'
import { Textarea } from '../ui.js'
import { Label, Panel, Tag } from './panes.js'
import type { Job } from './types.js'

const AUTOSAVE_MS = 800

export interface BriefValues {
  description: string | null
  scope: null
}

type SaveState = 'idle' | 'saving' | 'saved' | 'failed'

/**
 * The brief, as one box of words that saves itself.
 *
 * The text is `Job.description`, the column ServiceM8's paragraph was
 * imported into. A job made here may also have a `scope` from the New job
 * dialog or a template; it reads above the description, and the first save
 * folds both into `description` and clears `scope`. Who confirmed, the price,
 * the payment and where the paperwork goes are notes in the feed, not fields.
 * The 2026-09-24 amendment to decision 41.
 */
export function BriefPane({
  job,
  canEdit,
  onSave,
}: {
  job: Job
  canEdit: boolean
  onSave: (values: BriefValues) => Promise<unknown>
}) {
  // The order number sits here rather than in a paperwork panel of its own:
  // `Job.dueAt` is populated on none of the 68,359 imported jobs.
  const tags = [job.queue?.name].filter(Boolean) as string[]

  return (
    <Panel>
      {canEdit ? (
        <BriefText key={job.id} text={briefText(job)} onSave={onSave} />
      ) : (
        <>
          <Label>The brief</Label>
          <ReadOnlyBrief text={briefText(job)} />
        </>
      )}

      {job.workDone ? (
        <div className="flex flex-col gap-1">
          <Label>Work done</Label>
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-ink-700">{job.workDone}</p>
        </div>
      ) : null}

      {tags.length > 0 || job.purchaseOrderNumber ? (
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
          {job.purchaseOrderNumber ? (
            <Tag>
              Order <span className="font-mono font-semibold">{job.purchaseOrderNumber}</span>
            </Tag>
          ) : null}
        </div>
      ) : null}
    </Panel>
  )
}

function briefText(job: Pick<Job, 'scope' | 'description'>): string {
  return [job.scope, job.description]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join('\n\n')
}

function ReadOnlyBrief({ text }: { text: string }) {
  return text ? (
    <p className="text-sm leading-relaxed whitespace-pre-wrap text-ink-700">{text}</p>
  ) : (
    <p className="text-sm text-ink-400">Nobody wrote down what this job is.</p>
  )
}

/**
 * Keyed by the job, so a pending save on one job cannot be sent with the next
 * job's id: the unmount flush still holds the old `onSave`.
 */
function BriefText({
  text: serverText,
  onSave,
}: {
  text: string
  onSave: (values: BriefValues) => Promise<unknown>
}) {
  const [text, setText] = useState(serverText)
  const [state, setState] = useState<SaveState>('idle')

  // What the server holds, or is about to, trimmed the way the schema trims
  // it. Comparing against the trimmed text keeps a newline being typed from
  // reading as a change the server then takes away.
  const saved = useRef(serverText)
  const latest = useRef(text)
  const save = useRef(onSave)
  const timer = useRef<number | null>(null)
  const queue = useRef<Promise<unknown>>(Promise.resolve())

  latest.current = text
  save.current = onSave

  const flush = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
    const next = latest.current.trim()
    if (next === saved.current) return
    const previous = saved.current
    saved.current = next

    // In order, so a slow early save landing last cannot undo a later one.
    queue.current = queue.current
      .then(async () => {
        setState('saving')
        await save.current({ description: next === '' ? null : next, scope: null })
        setState('saved')
      })
      .catch(() => {
        if (saved.current === next) saved.current = previous
        setState('failed')
      })
  }

  // Someone else changed the brief. Take it, unless this box is mid-edit —
  // re-seeding then would discard the typing.
  useEffect(() => {
    if (serverText === saved.current) return
    if (latest.current.trim() !== saved.current) return
    saved.current = serverText
    setText(serverText)
  }, [serverText])

  useEffect(() => {
    if (text.trim() === saved.current) return
    timer.current = window.setTimeout(flush, AUTOSAVE_MS)
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- flush reads refs
  }, [text])

  // eslint-disable-next-line react-hooks/exhaustive-deps -- flush reads refs
  useEffect(() => () => flush(), [])

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2.5">
        <label htmlFor="job-brief">
          <Label>The brief</Label>
        </label>
        <span className="ml-auto text-xs text-ink-400" aria-live="polite">
          {state === 'saving'
            ? 'Saving…'
            : state === 'saved'
              ? 'Saved'
              : state === 'failed'
                ? 'Not saved — keep typing to retry'
                : null}
        </span>
      </div>
      <Textarea
        id="job-brief"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onBlur={flush}
        placeholder="What this job is, and anything the technician should know."
        className="min-h-20 bg-ink-50/70 ring-transparent [field-sizing:content] leading-relaxed hover:ring-ink-200 focus:bg-white"
      />
    </div>
  )
}
